import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ArrowClockwise, Check } from "@phosphor-icons/react";

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        if (!src.startsWith("blob:")) img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

async function getCroppedBlob(imageSrc, cropPixels, rotation) {
    const image = await loadImage(imageSrc);
    const rotRad = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rotRad));
    const cos = Math.abs(Math.cos(rotRad));
    const rotatedW = image.width * cos + image.height * sin;
    const rotatedH = image.width * sin + image.height * cos;

    const rotCanvas = document.createElement("canvas");
    rotCanvas.width = Math.round(rotatedW);
    rotCanvas.height = Math.round(rotatedH);
    const rctx = rotCanvas.getContext("2d");
    rctx.translate(rotCanvas.width / 2, rotCanvas.height / 2);
    rctx.rotate(rotRad);
    rctx.drawImage(image, -image.width / 2, -image.height / 2);

    const MAX_SIDE = 1600;
    const scale = Math.min(1, MAX_SIDE / Math.max(cropPixels.width, cropPixels.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(cropPixels.width * scale);
    canvas.height = Math.round(cropPixels.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
        rotCanvas,
        cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
        0, 0, canvas.width, canvas.height
    );
    return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
}

export default function ImageEditorModal({ open, imageSrc, aspect = 4 / 3, title = "Edit Foto", onCancel, onSave }) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [saving, setSaving] = useState(false);

    const onCropComplete = useCallback((_, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const handleSave = async () => {
        if (!croppedAreaPixels) return;
        setSaving(true);
        try {
            const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, rotation);
            await onSave(blob);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
            <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-2xl" data-testid="image-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display uppercase tracking-wide text-xl">{title}</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-racing-muted">Geser foto untuk mengatur posisi, gunakan slider untuk zoom, dan tombol putar untuk rotasi. Rasio crop mengikuti tampilan di website.</p>

                <div className="relative w-full h-[380px] bg-neutral-900 mt-3" data-testid="image-editor-cropper">
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            rotation={rotation}
                            aspect={aspect}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onRotationChange={setRotation}
                            onCropComplete={onCropComplete}
                        />
                    )}
                </div>

                <div className="flex items-center gap-4 mt-4">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Zoom</span>
                    <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.05}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        data-testid="image-editor-zoom"
                        className="flex-1 accent-[#D21E06]"
                    />
                    <button
                        type="button"
                        onClick={() => setRotation((r) => (r + 90) % 360)}
                        data-testid="image-editor-rotate"
                        className="flex items-center gap-2 border border-racing-line px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-neutral-800 hover:border-racing-red hover:text-racing-red transition-colors"
                    >
                        <ArrowClockwise size={16} weight="bold" /> Putar 90°
                    </button>
                </div>

                <div className="flex gap-3 mt-5">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="image-editor-cancel"
                        className="flex-1 border border-racing-line text-racing-muted hover:text-neutral-900 text-xs uppercase tracking-[0.2em] py-3.5 transition-colors"
                    >
                        Batal
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="image-editor-save"
                        className="flex-1 flex items-center justify-center gap-2 bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.2em] py-3.5 transition-colors"
                    >
                        <Check size={16} weight="bold" /> {saving ? "Memproses..." : "Gunakan Foto Ini"}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
